import { useMemo } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";

export const PAGE_SIZE = 20;

/**
 * usePagination — slices an array into the visible page.
 * Returns { rows, page, setPage, total, pages }.
 */
export function paginate(items, page, size = PAGE_SIZE) {
  const total = items.length;
  const pages = Math.max(1, Math.ceil(total / size));
  const safePage = Math.min(Math.max(1, page), pages);
  const start = (safePage - 1) * size;
  return {
    rows: items.slice(start, start + size),
    page: safePage,
    pages,
    total,
    start,
    end: Math.min(start + size, total),
  };
}

export default function Pagination({ page, pages, total, start, end, onChange, testid = "pagination" }) {
  const visiblePages = useMemo(() => {
    const out = [];
    const max = 5;
    let from = Math.max(1, page - 2);
    let to = Math.min(pages, from + max - 1);
    from = Math.max(1, to - max + 1);
    for (let i = from; i <= to; i++) out.push(i);
    return out;
  }, [page, pages]);

  if (total === 0) return null;

  return (
    <div className="mt-4 flex items-center justify-between gap-3 px-1" data-testid={testid}>
      <div className="text-xs text-slate-500">
        Affichage <span className="font-semibold text-slate-700">{start + 1}–{end}</span> sur{" "}
        <span className="font-semibold text-slate-700">{total}</span>
      </div>
      {pages > 1 && (
        <div className="flex items-center gap-1">
          <button
            onClick={() => onChange(page - 1)}
            disabled={page <= 1}
            className="h-8 w-8 inline-flex items-center justify-center rounded-md border border-slate-200 text-slate-600 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            data-testid={`${testid}-prev`}
            aria-label="Page précédente"
          >
            <ChevronLeft className="w-4 h-4" />
          </button>
          {visiblePages[0] > 1 && (
            <>
              <button onClick={() => onChange(1)} className="h-8 px-2.5 rounded-md text-xs font-medium text-slate-600 hover:bg-slate-100">1</button>
              {visiblePages[0] > 2 && <span className="text-slate-400 text-xs">…</span>}
            </>
          )}
          {visiblePages.map((p) => (
            <button
              key={p}
              onClick={() => onChange(p)}
              className={`h-8 min-w-[32px] px-2.5 rounded-md text-xs font-semibold transition-colors ${
                p === page
                  ? "bg-orange-500 text-white shadow-sm"
                  : "text-slate-600 hover:bg-slate-100"
              }`}
              data-testid={`${testid}-page-${p}`}
            >
              {p}
            </button>
          ))}
          {visiblePages[visiblePages.length - 1] < pages && (
            <>
              {visiblePages[visiblePages.length - 1] < pages - 1 && <span className="text-slate-400 text-xs">…</span>}
              <button onClick={() => onChange(pages)} className="h-8 px-2.5 rounded-md text-xs font-medium text-slate-600 hover:bg-slate-100">{pages}</button>
            </>
          )}
          <button
            onClick={() => onChange(page + 1)}
            disabled={page >= pages}
            className="h-8 w-8 inline-flex items-center justify-center rounded-md border border-slate-200 text-slate-600 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            data-testid={`${testid}-next`}
            aria-label="Page suivante"
          >
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      )}
    </div>
  );
}
